import {
  Component,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef
, ChangeDetectionStrategy } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import screenfull from 'screenfull';
import { Screenfull } from 'screenfull';

@Component({
    selector: 'app-fullscreen',
    templateUrl: './fullscreen.component.html',
    styleUrls: ['./fullscreen.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FullscreenComponent implements OnInit, OnDestroy {
  @Input() public isDownloadLoading = false;
  @Input() public showFullScreenBtn = true;
  @Input() public template: TemplateRef<any>;
  @Input() public showDownloadBtn = false;

  @Output() public fullscreenToggle: EventEmitter<boolean> = new EventEmitter();
  @Output() public downloadFile: EventEmitter<void> = new EventEmitter();

  @ViewChild('targetTmp', { static: false }) public targetTmp: ElementRef<any>;
  @ViewChild('fullscreenTmp', { static: false }) fullscreenTmp: TemplateRef<any>;

  public fulscreenMode = false;

  public isFullscreenDisabled = false;

  private embeddedViewRef: EmbeddedViewRef<any>;
  private bodyClass = 'enable-fullscreen-mode';

  private backButtonListener: PluginListenerHandle | null = null;

  constructor(
    private readonly element: ElementRef,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly renderer2: Renderer2) { }

  ngOnInit() {
  }

  public download(): void {
    this.downloadFile.emit();
  }

  public fullScreenModelToggle(): void {
    if (this.fulscreenMode && this.isFullscreenDisabled) {
      this.exitFromFullScreenMode();
      return;
    }

    this.fulscreenMode = true;

    if (!Capacitor.isNativePlatform()) {
      const sf = screenfull as Screenfull;

      if (sf.isEnabled) {
        sf.toggle(this.targetTmp.nativeElement).then(() => {
          this.fulscreenMode = false;
        });

        return;
      }
    }

    this.isFullscreenDisabled = true;
    this.openCustomFullscreen();
  }

  public exitFromFullScreenMode(): void {
    if (!this.fulscreenMode) {
      return;
    }

    this.fulscreenMode = false;

    if (!Capacitor.isNativePlatform()) {
      const sf = screenfull as Screenfull;

      if (sf.isEnabled) {
        sf.exit();
        return;
      }
    }

    this.isFullscreenDisabled = false;
    this.closeCustomFullscreen();
  }

  private openCustomFullscreen(): void {
    this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.fullscreenTmp);
    this.embeddedViewRef.detectChanges();

    const node = this.embeddedViewRef.rootNodes[0] as HTMLElement;
    this.renderer2.appendChild(node, this.targetTmp.nativeElement);
    this.renderer2.appendChild(document.body, node);
    this.toggleScrollLock(true);
    this.renderer2.addClass(document.body, this.bodyClass);
    this.startBackButtonListener();
    this.fullscreenToggle.emit(true);
  }

  private closeCustomFullscreen(): void {
    this.stopBackButtonListener();
    this.renderer2.removeClass(document.body, this.bodyClass);
    this.toggleScrollLock(false);
    this.renderer2.appendChild(this.element.nativeElement, this.targetTmp.nativeElement);
    this.embeddedViewRef?.destroy();
    this.fullscreenToggle.emit(false);
  }

  private toggleScrollLock(enable: boolean): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const html = document.documentElement;

    if (enable) {
      this.renderer2.addClass(html, 'disable-scroll');
    } else {
      this.renderer2.removeClass(html, 'disable-scroll');
    }
  }

  private async startBackButtonListener(): Promise<void> {
    if (!Capacitor.isNativePlatform()) { return; }

    // prevent multiple listeners
    if (this.backButtonListener) { return; }

    this.backButtonListener = await App.addListener('backButton', () => {
      if (!this.fulscreenMode) {
        return;
      }

      this.exitFromFullScreenMode();
    });
  }

  private stopBackButtonListener(): void {
    if (!this.backButtonListener) {
      return;
    }
    
    this.backButtonListener?.remove();
    this.backButtonListener = null;
  }

  ngOnDestroy(): void {
    this.stopBackButtonListener();
  }
}
